import type { MouseEvent, TouchEvent } from 'react';
import { useMemo, useReducer, useState } from 'react';
import { Cross2Icon, ImageIcon, PlusIcon, VideoIcon } from '@radix-ui/react-icons';
import { Button, Dialog, IconButton, Spinner, Switch, TextArea, TextField } from '@radix-ui/themes';
import { checkText } from 'smile2emoji';
import { currentUser, isLoggedIn, userIsFitPlayerProfile } from 'helpers/authentication';
import { alertModal, confirmationModal } from 'helpers/confirmation';
import currentProfile from 'helpers/currentProfile';
import { reducer } from 'helpers/utils';
import { createPost, setNewPost } from 'store/newPost/newPostSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import type { ImageType, PostAuthorType } from 'types/post';
import ModalNoCard from './ModalNoCard';
import PhotoSelector from './PhotoSelector';

const NewPostButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const initialState = {
    asAdmin: false,
    images: [],
    video: '',
    showController: false,
    showImagesController: false,
    showVideoController: false,
  };
  const { content, loading, sharedPost } = useAppSelector((state) => state.newPost);
  const currProfile = useAppSelector((state) => state.profileSelector.currentProfile);
  const profile = useMemo(() => currentProfile({ index: currProfile }), [currProfile]);
  const [state, dispatchState] = useReducer(reducer, initialState);
  const [modalNoCardOpen, setModalNoCardOpen] = useState(false);

  const handleImageSelection = (id: number) => (newUrl: string | null | ArrayBuffer, oldUrl: string | null) => {
    if (newUrl) {
      const newImage = { id: null, url: newUrl, _destroy: false };
      dispatchState({ type: 'images', payload: state.images.concat(newImage) });
    } else {
      if (oldUrl) {
        const newImages = state.images.map((image: ImageType) => {
          if (id) {
            return image.id === id ? { ...image, _destroy: true } : image;
          } else {
            return image.url === oldUrl ? { ...image, _destroy: true } : image;
          }
        });
        dispatchState({ type: 'images', payload: newImages });
      }
    }
  };

  const renderImages = () => {
    const nonDestroyedImages = [...state.images.filter((image: ImageType) => !image._destroy), ''];
    if (nonDestroyedImages.length) {
      return (
        <div className=''>
          <div className='flex gap-5'>
            {nonDestroyedImages.map(({ id, url }, index) => (
              <div key={`image-${index}`}>
                <PhotoSelector url={url} onChange={handleImageSelection(id)} />
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  const renderVideoInput = () => (
    <div className=''>
      <div className='flex gap-5'>
        <div className='w-auto'>
          <label>Youtube URL</label>
        </div>
        <div className='min-w-px flex-1'>
          <TextField.Root
            type='text'
            value={state.video}
            onChange={(e) => dispatchState({ type: 'video', payload: e.target.value })}
          />
        </div>
      </div>
    </div>
  );

  const onImagesControllerClick = () => {
    if (state.showImagesController) {
      dispatchState({ type: 'showController', payload: false });
      dispatchState({ type: 'showImagesController', payload: false });
      dispatchState({ type: 'images', payload: [] });
    } else {
      dispatchState({ type: 'showController', payload: true });
      dispatchState({ type: 'showImagesController', payload: true });
    }
  };

  const onVideoControllerClick = () => {
    if (state.showVideoController) {
      dispatchState({ type: 'showController', payload: false });
      dispatchState({ type: 'showVideoController', payload: false });
    } else {
      dispatchState({ type: 'showController', payload: true });
      dispatchState({ type: 'showVideoController', payload: true });
    }
  };

  const clearImagesAndVideoStuff = () => {
    dispatchState({ type: 'showController', payload: false });
    dispatchState({ type: 'showVideoController', payload: false });
    dispatchState({ type: 'showImagesController', payload: false });
    dispatchState({ type: 'images', payload: [] });
    dispatchState({ type: 'video', payload: '' });
  };

  if (!isLoggedIn()) return null;

  const checkCardFunction = (e: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>) => {
    if (!userIsFitPlayerProfile()) {
      e.preventDefault();
      e.stopPropagation();
      setModalNoCardOpen(true);
    }
  };

  return modalNoCardOpen ? (
    <ModalNoCard text='Per poter scrivere post' open={modalNoCardOpen} setOpen={setModalNoCardOpen} />
  ) : (
    <Dialog.Root>
      <Dialog.Trigger>
        <div className='fixed right-4 bottom-24 z-10 md:right-10 md:bottom-10'>
          <IconButton
            className='border-accent-accent-8 cursor-pointer rounded-md border border-solid'
            aria-label='Nuovo post'
            onClick={checkCardFunction}
            onTouchStart={checkCardFunction}
          >
            <PlusIcon />
          </IconButton>
        </div>
      </Dialog.Trigger>
      <Dialog.Content maxWidth='450px'>
        <div className='flex w-full justify-between align-middle'>
          <Dialog.Title>Pubblica un nuovo post</Dialog.Title>
          <Dialog.Close className='mb-4 cursor-pointer'>
            <Cross2Icon height={'24'} width={'24'} />
          </Dialog.Close>
        </div>
        <Dialog.Description className='hidden'>Pubblica un nuovo post</Dialog.Description>
        <div className='flex flex-col gap-5'>
          <TextArea
            value={checkText(content ?? '')}
            rows={2}
            disabled={loading}
            placeholder={`${profile?.name} scrivi qualcosa...`}
            className='w-full flex-initial'
            onChange={(event) => dispatch(setNewPost(checkText(event.target.value)))}
          />

          {state.showImagesController && renderImages()}

          {state.showVideoController && renderVideoInput()}

          <div className=''>
            <div className='flex items-center gap-3'>
              {state.showController ? (
                <Cross2Icon className='cursor-pointer' onClick={clearImagesAndVideoStuff} />
              ) : (
                <>
                  <div className='w-auto'>
                    <ImageIcon className='cursor-pointer' onClick={onImagesControllerClick} />
                  </div>

                  <div className='w-auto'>
                    <VideoIcon className='cursor-pointer' onClick={onVideoControllerClick} />
                  </div>
                </>
              )}

              <div className='min-w-px flex-1 text-right'>
                <Button
                  className='cursor-pointer'
                  disabled={
                    ((content ?? '').trim() === '' &&
                      !state.images.filter((image: ImageType) => !image._destroy).length &&
                      !state.video) ||
                    loading
                  }
                  onClick={() => {
                    if (
                      content &&
                      content?.split(' ').length <= 2 &&
                      !state.images.filter((image: ImageType) => !image._destroy).length &&
                      !state.video
                    ) {
                      alertModal('Il post è troppo corto, per pubblicarlo devi scrivere più contenuti');
                    } else if (content?.includes('armonizzat')) {
                      confirmationModal(
                        'Ti ricordiamo che dire ad altri utenti la loro CLASSIFICA ARMONIZZATA è contro i termini di servizio e può portare al ban. Vuoi pubblicare il contenuto?',
                        () => {
                          dispatch(
                            createPost({
                              content,
                              profile: profile as PostAuthorType,
                              sharedPostId: sharedPost ? sharedPost.id : null,
                              asAdmin: state.asAdmin,
                              images: state.images.filter((image: ImageType) => !image._destroy),
                              video: state.video,
                              callBack: () => (window.location.href = '/feed'),
                            }),
                          );
                        },
                      );
                    } else {
                      dispatch(
                        createPost({
                          content,
                          profile: profile as PostAuthorType,
                          sharedPostId: sharedPost ? sharedPost.id : null,
                          asAdmin: state.asAdmin,
                          images: state.images.filter((image: ImageType) => !image._destroy),
                          video: state.video,
                          callBack: () => (window.location.href = '/feed'),
                        }),
                      );
                    }

                    clearImagesAndVideoStuff();
                  }}
                >
                  {loading ? <Spinner /> : 'Pubblica'}
                </Button>
              </div>
            </div>

            {currentUser().role === 'admin' && (
              <div className='flex items-center'>
                <div className='mt-3 flex min-w-px flex-1 gap-3'>
                  <Switch
                    checked={state.asAdmin}
                    className='cursor-pointer'
                    onCheckedChange={(asAdmin: boolean) => dispatchState({ type: 'asAdmin', payload: asAdmin })}
                  />
                  Pubblica come TennisTalker
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default NewPostButton;
